import { useCallback, useEffect, useRef, useState } from "react";
import { EmailDetail, GetEmailDetail, isInboundDetail, SendEmailAction } from "./EmailDetailAPI";
import { EmailRef, ProcessingStatus } from "./EmailSearchAPI";
import { searchErrorMessages } from "./searchErrors";

/**
 * The six ways reading one email can end.
 *
 * `notFound` has no equivalent in the worklist and is genuinely reachable
 * here: a row can be opened from a page of results minted before the email
 * was purged, and the operator needs the way back rather than an error panel.
 *
 * `forbidden` keeps the worklist's meaning — elevation into the tenant
 * WORKED and the tenant's own API still refused, which indicts a different
 * system from a failed mint. A mint `403` is `failed`: by the time a row is
 * open, entitlement was already proven by the search that produced it, so a
 * refusal now is a fault rather than a standing answer.
 *
 * A mint `401` is NOT covered by that argument — see `stateForMintError`.
 */
export type DetailState = "loading" | "ready" | "notFound" | "forbidden" | "unauthorised" | "failed";

/**
 * What came back from the last action, if one has been taken.
 *
 * `refused` and `failed` are SEPARATE, and the view renders them differently
 * (spec §7.2). A `400` means the server declined a precondition — nothing
 * changed, and in one case nothing is even wrong ("a send failure retries
 * automatically"). Rendering that in the same red as a `500` misdescribes it.
 */
export type ActionResult =
    { kind: "queued"; newStatus: ProcessingStatus } | { kind: "refused"; messages: string[] } | { kind: "failed" };

export interface iUseEmailDetailProps {
    /** Which email. Carried as a unit; see contract §2.7. */
    emailRef: EmailRef;
    tenantKey: string;
    /** From `useTenantToken`. Must be identity-stable or this refetches forever. */
    tokenFor: (tenantKey: string) => Promise<string>;
}

export interface iUseEmailDetail {
    state: DetailState;
    detail?: EmailDetail;
    /** True while re-reading over something already on screen. Never on a first load. */
    refreshing: boolean;
    refresh: () => void;
    /**
     * True once any action has been accepted.
     *
     * The worklist behind this page is now stale — a reprocessed email that
     * still reads `Failed` in the row behind it is a lie the `as of` stamp
     * does not excuse. The parent reads this on the way back and re-runs the
     * search only when it is true.
     */
    acted: boolean;
    /** True while an action is in flight. */
    sending: boolean;
    /** The last action's outcome. Undefined until one is taken. */
    result?: ActionResult;
    /** Queue this email's action. The reason is optional. */
    submit: (reason?: string) => Promise<void>;
}

/**
 * Same-email-by-value, for the same reason the render-phase reset below draws
 * the comparison this way: identity is not stable across renders.
 */
function sameEmail(a: EmailRef, b: EmailRef): boolean {
    return a.direction === b.direction && a.id === b.id;
}

/**
 * Maps `getTenantAuth`'s throws onto states. It throws bare `Error("403")` /
 * `Error("401")` message strings rather than typed errors, the same way
 * `useEmailSearch` and `useTenantInspection` have to read them.
 *
 * A `403` is deliberately NOT `forbidden` and NOT a standing answer about
 * access: reaching this page required a search that already proved
 * entitlement, so a refusal at the mint now is a fault. It falls through to
 * `failed`, which is why this function names only the one case.
 *
 * That argument does not extend to a `401`. Session expiry says nothing about
 * entitlement, and it is MORE likely here than on the worklist: the operator
 * reads a timeline for a while and then presses Refresh, by which point the
 * session behind the mint may well have gone. Spec §6 already gives that its
 * own copy ("Your session has expired. Reload the page to sign in again.");
 * collapsing it into `failed` shows "This email could not be read" over an
 * expired session and sends the operator looking for a server fault.
 */
function stateForMintError(error: unknown): DetailState {
    if (error instanceof Error && error.message === "401") {
        return "unauthorised";
    }
    return "failed";
}

function stateForStatus(status: number): DetailState {
    if (status === 404) {
        return "notFound";
    }
    if (status === 403) {
        return "forbidden";
    }
    if (status === 401) {
        return "unauthorised";
    }
    return "failed";
}

/**
 * One email, with its timeline, and the support action taken against it.
 */
export function useEmailDetail({ emailRef, tenantKey, tokenFor }: iUseEmailDetailProps): iUseEmailDetail {
    const [state, setState] = useState<DetailState>("loading");
    const [detail, setDetail] = useState<EmailDetail | undefined>(undefined);
    const [refreshing, setRefreshing] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const [acted, setActed] = useState(false);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState<ActionResult | undefined>(undefined);

    // Bumped on entry and by the effect cleanup, checked after every await, so
    // a refresh issued while an earlier read is in flight cannot let the older
    // response land. Same shape as useEmailSearch's counter.
    const requestId = useRef(0);

    /** False until the first request settles, so a first load is `loading`, not `refreshing`. */
    const hasSettled = useRef(false);

    // Adjusting state during render, React's documented pattern for "a prop
    // changed and some state derived from it is now wrong" — the same pattern
    // useEmailSearch draws for a tenant switch. Compared BY VALUE (`direction`
    // and `id`), never by identity: `emailRef` is a fresh object on every
    // render for a caller that builds it from other state (e.g. `{ direction,
    // id }` inline), and an identity-only comparison would reset on every
    // such render even though nothing about the email being asked for
    // actually changed.
    const [lastEmailRef, setLastEmailRef] = useState(emailRef);
    if (!sameEmail(emailRef, lastEmailRef)) {
        setLastEmailRef(emailRef);

        // `emailRef` is already an effect dependency, so pointing the hook at
        // a different email re-runs the read on its own. But without this,
        // nothing resets `detail` or `state`: for the whole round trip the
        // hook would report `state: "ready"` carrying the PREVIOUS email's
        // subject, timeline and `asOf` under the NEW ref, with `refreshing`
        // going `true` rather than `state` going back to `loading`. That is
        // the same defect useEmailSearch's tenant-switch reset exists to
        // prevent: carrying one key's data across to a different key for a
        // whole round trip, with nothing marking it stale.
        setDetail(undefined);
        setState("loading");

        // The write half needs the identical reset, for the identical reason:
        // `result` and `sending` describe the PREVIOUS email's action just as
        // much as `detail` describes its content, and nothing else clears
        // them when the hook moves on. Leave either one and the failure is
        // the same shape as leaving `detail` uncleared above — B ends up
        // rendering a description of an action taken against A: `result`
        // carries A's "queued" banner (or A's refusal) onto B's screen, and
        // `sending` disables B's action control for an action that was never
        // taken against B, then silently re-enables once A's own request
        // happens to settle. `submit`'s `finally` is deliberately NOT guarded
        // with `isStillOnScreen()` to fix this from the other side — that
        // would strand `sending` at `true` forever whenever the switch
        // happens mid-flight — so this reset is what has to make B correct
        // instead.
        //
        // `acted` is DELIBERATELY excluded from this reset. Read the comment
        // on it below before adding it here: the same argument that justifies
        // clearing `result` and `sending` does not apply to it.
        setResult(undefined);
        setSending(false);

        // Reset WITH the state above, never separately. `refreshing` is read
        // from this ref on effect entry, so a "loading" state over a settled
        // ref would report loading and refreshing at once — and their being
        // mutually exclusive is what stops a header reading "Refreshing…"
        // over a page with nothing on it.
        hasSettled.current = false;

        // `acted` MUST NOT be cleared here, and the temptation to add it once
        // it becomes stateful is the trap this comment exists to close.
        //
        // It does not mean "this email was acted on". It means "something has
        // been written since the worklist behind us was read", which is why
        // the parent consults it to decide whether to re-run the search on the
        // way back. Reprocess email A, move to email B, then go back: the
        // worklist is still stale — A's row still reads Failed — and clearing
        // the flag on the move would suppress exactly the refresh that lie
        // calls for.
    }

    // What the hook is CURRENTLY showing, kept live for `submit` to compare
    // against once its `await`s resolve. Assigned unconditionally, every
    // render — not only inside the reset branch above — because `emailRef`
    // is the same value on a render that takes that branch and one that
    // doesn't; either way this ref must end up holding it. A ref rather than
    // reading `emailRef`/`detail` from `submit`'s own closure: those are
    // frozen at the render that created the callback, and by the time a
    // queued action's response lands the operator may have moved the hook on
    // to a different email several renders later.
    const currentEmailRef = useRef(emailRef);
    currentEmailRef.current = emailRef;

    // What `submit` reads at call time instead of closing over — the same
    // discipline `useTenantToken`'s returned function holds itself to, and
    // for the same reason (see its file). `detail` becomes a new object
    // after every read, every refresh, and after `submit`'s OWN optimistic
    // apply below, so closing over it — or over `tenantKey`/`tokenFor` —
    // would give `submit` a new identity on every one of those. That is
    // harmless for a click handler, but a consumer that puts `submit` in a
    // `useEffect` dependency array gets an effect that re-runs constantly,
    // and one that calls `submit` from inside that effect self-triggers:
    // `submit` → `setDetail` → new `submit` → effect → `submit` again, an
    // unbounded POST loop against a live tenant endpoint. Reading fresh
    // values here also closes a stale-closure hazard for free: a consumer
    // holding on to an old `submit` would otherwise queue an action using a
    // previous email's ref.
    const submitInputs = useRef({ detail, tenantKey, tokenFor });
    submitInputs.current = { detail, tenantKey, tokenFor };

    // Re-entrancy guard for `submit`, below. A ref rather than the `sending`
    // state: two calls issued in the same tick (a double-click before React
    // re-renders, or two independent callers) would both read state's stale
    // `false` and both pass a `sending` check, each firing its own
    // `SendEmailAction` POST and queueing the same action twice. Set before
    // the first `await` inside `submit`, so the second call's synchronous
    // check runs after the first call's is already true; cleared in
    // `submit`'s existing `finally`.
    const submitInFlight = useRef(false);

    useEffect(() => {
        requestId.current += 1;
        const thisRequest = requestId.current;
        const isCurrent = () => requestId.current === thisRequest;

        // `refreshing` means "re-reading over something already on screen", so
        // it stays false until a request has actually settled. Before that
        // `state` is still "loading" and the two would otherwise both be true,
        // which reads as "Refreshing…" on a page that has never loaded.
        setRefreshing(hasSettled.current);

        void (async () => {
            try {
                let token: string;
                try {
                    token = await tokenFor(tenantKey);
                    if (!isCurrent()) {
                        return;
                    }
                } catch (error) {
                    // Not `notEntitled`. Reaching this page required a search
                    // that already proved entitlement, so a refusal now is a
                    // fault rather than a standing answer about access — but
                    // only a `403` is that refusal. A `401` is session expiry
                    // and keeps its own state; see `stateForMintError`.
                    if (isCurrent()) {
                        setState(stateForMintError(error));
                    }
                    return;
                }

                const response = await GetEmailDetail(token, emailRef);
                if (!isCurrent()) {
                    return;
                }

                // Unlike a search's empty page, a `200` with no body is not a
                // valid outcome for a single email — there is no "nothing
                // matched" reading for a drill-down into one specific ref.
                // `stateForStatus(200)` falls through to `failed`, which is
                // the right answer here rather than a state that needs its
                // own handling.
                if (!response.isOk || !response.data) {
                    setState(stateForStatus(response.status));
                    return;
                }

                setDetail(response.data);
                setState("ready");
            } finally {
                // try/finally rather than a check at each return: there are
                // several exit paths, and a flag left stuck true after any one
                // of them permanently disables the refresh control.
                if (isCurrent()) {
                    hasSettled.current = true;
                    setRefreshing(false);
                }
            }
        })();

        return () => {
            requestId.current += 1;
        };
    }, [emailRef, tenantKey, tokenFor, reloadToken]);

    const refresh = useCallback(() => setReloadToken(token => token + 1), []);

    const submit = useCallback(
        async (reason?: string) => {
            // Read live, from `submitInputs`, not from this closure — see the
            // comment where that ref is assigned. That is what keeps `submit`
            // identity-stable below.
            const { detail, tenantKey, tokenFor } = submitInputs.current;

            // Nothing to act on. Reachable only if a view renders the control
            // outside `ready`, which none does — belt and braces so a future
            // one cannot POST against an email it never read.
            if (!detail) {
                return;
            }

            // Belt and braces alongside `ActionBar`'s own unmount-on-click:
            // `useEmailDetail` owns `sending` and issues the POST, so the
            // guard belongs here rather than being delegated to a component's
            // unmount timing. See the comment on `submitInFlight` above.
            if (submitInFlight.current) {
                return;
            }
            submitInFlight.current = true;

            // Captured before any `await`, then compared BY VALUE against
            // `currentEmailRef.current` after each one.
            //
            // Without this, an action queued against email A that resolves
            // after the operator has already pointed the hook at email B
            // would merge A's `newStatus` and journaled event onto B's
            // `detail` — the render-phase reset above means B's read has by
            // then replaced `undefined` with real data, so the
            // `current === undefined` check inside the optimistic apply no
            // longer catches it — and raise a "queued" banner for an action
            // the operator does not associate with B. `refused` and `failed`
            // are guarded the same way: both are banners about the email on
            // screen, and a refusal for A must not appear over B.
            //
            // `acted` is DELIBERATELY left outside this guard below — see the
            // long comment on it in the render-phase reset block. The write
            // to A really happened, so the worklist really is stale
            // regardless of what is on screen when the response lands;
            // suppressing the flag here would suppress exactly the refresh
            // that staleness calls for. Do not "fix" this by wrapping it in.
            const requestedRef = detail.ref;
            const isStillOnScreen = () => sameEmail(currentEmailRef.current, requestedRef);

            setSending(true);
            // Cleared on entry so a second attempt never shows the previous
            // attempt's banner while it is in flight.
            setResult(undefined);

            try {
                let token: string;
                try {
                    token = await tokenFor(tenantKey);
                } catch {
                    // Deliberately NOT split on `401` the way the read's mint
                    // catch above is. This half reports through `ActionResult`,
                    // which describes an attempted WRITE, and every mint
                    // rejection means the same thing about that write: it never
                    // reached the server, so nothing changed — exactly what the
                    // failed banner says. Reaching for `stateForMintError` here
                    // would have to write `state`, which drives the whole page:
                    // it would replace the email, its timeline and its card with
                    // the session-expired notice on a failure that changed
                    // nothing, discarding what the operator was reading. The
                    // expiry surfaces with the right copy on their next read,
                    // where a full-page notice is the correct answer.
                    if (isStillOnScreen()) {
                        setResult({ kind: "failed" });
                    }
                    return;
                }

                const response = await SendEmailAction(token, detail.ref, reason);

                if (!response.isOk || !response.data) {
                    // A 400 is a precondition refusal: the server declined and
                    // NOTHING changed. Its own sentences are rendered verbatim
                    // — deliberately not matched against to style each one
                    // individually, because matching on server prose breaks
                    // silently the day somebody fixes a typo there.
                    //
                    // `searchErrorMessages` already ignores the key namespace,
                    // so it handles `DataLinkID` and `MessageID` unchanged.
                    if (isStillOnScreen()) {
                        setResult(
                            response.status === 400
                                ? { kind: "refused", messages: searchErrorMessages(response.validationErrors) }
                                : { kind: "failed" },
                        );
                    }
                    return;
                }

                const { newStatus, event, asOf } = response.data;

                // THE OPTIMISTIC APPLY (contract §2.8). `ActionAccepted` carries
                // everything needed to reflect the write, so this costs no round
                // trip — but the write moves MORE than the status, and applying
                // only the status and the timeline leaves the rest of the card
                // describing the read that happened before the action.
                //
                // `stalled: false` is NOT this file re-deriving the overlay,
                // which contract §2.3 forbids. It is a fact the server has just
                // guaranteed: `MailProcessingTracker.UpdateStatusStrictAsync`
                // sets `UpdatedAt = now` on every accepted write, in both
                // directions, and `StalledEvaluator.IsStalled` returns false
                // unless `now - UpdatedAt` exceeds the status's threshold — so
                // the server's own read-time answer is false by construction at
                // this instant. No threshold is consulted here; the value is
                // asserted from the write, not computed.
                //
                // Carrying the read's `stalled: true` through instead is what
                // breaks the single case Reprocess mainly exists for. A stalled
                // inbound `Queued`/`Processing` is eligible through
                // `emailActions`' stalled clause, so the flip to `Queued` would
                // re-enable the control beside the "Queued" banner, still badged
                // Stalled — and a second confirm hits a handler that recomputes
                // `stalled` against the `UpdatedAt` it has just moved and answers
                // `400 "Email is not in a reprocessable state."` With `stalled`
                // cleared the status flip disables the control by itself,
                // through the same eligibility rule that gated it — there is no
                // separate "submitted" flag to fall out of sync.
                //
                // `asOf` moves with it: the header's "as of" stamp otherwise
                // still reports the read while the timeline entry appended by
                // this very response is stamped later than the page claims to
                // have been read.
                //
                // `processingUpdatedAt` ("Last activity") takes
                // `event.occurredAt`, NOT `asOf`. Both describe the same instant
                // — the request stamp the server just wrote to `UpdatedAt` — but
                // `asOf` carries an offset and the row timestamps do not
                // (contract §1.3), and this field is rendered by the same helper
                // as every other row timestamp. Mixing the two formats is the
                // thing that rule exists to stop.
                //
                // `attemptCount` is zeroed only on `Sendable`, which names
                // exactly one path: `RetryOutboundMailHandler.ReArmAsync` is the
                // only place that returns it, and it is the only caller of
                // `UpdateStatusStrictAsync(…, resetSendAttempts: true)`. Every
                // other accepted action — inbound Reprocess, the outbound
                // rebuild — returns `Queued` and leaves the counter alone, so a
                // page still reading "Attempts 8" after a re-arm is wrong and
                // one reading it after a rebuild is right.
                if (isStillOnScreen()) {
                    setDetail(current => {
                        if (current === undefined) {
                            return current;
                        }

                        const applied: EmailDetail = {
                            ...current,
                            status: newStatus,
                            stalled: false,
                            asOf,
                            processingUpdatedAt: event.occurredAt,
                            timeline: [...(current.timeline ?? []), event],
                        };

                        // Narrowed rather than tested on `ref.direction`, which
                        // is a nested discriminant and narrows nothing — the
                        // same reason `isInboundDetail` exists at all. It is
                        // also the honest guard: `attemptCount` is an outbound
                        // field, and `Sendable` on an inbound detail would be a
                        // foreign status rather than a re-arm.
                        return isInboundDetail(applied) || newStatus !== "Sendable"
                            ? applied
                            : { ...applied, attemptCount: 0 };
                    });
                    setResult({ kind: "queued", newStatus });
                }
                setActed(true);
            } finally {
                setSending(false);
                submitInFlight.current = false;
            }
        },
        // Permanently identity-stable — the same property `useTenantToken`'s
        // returned function has, and for the same reason: everything it needs
        // is read fresh from `submitInputs` at call time above, so nothing
        // here should ever force React to hand out a new function.
        [],
    );

    return { state, detail, refreshing, refresh, acted, sending, result, submit };
}
